import { Link } from "react-router-dom";
import {
  ArrowRight,
  FileCheck2,
  MapPin,
  Scale,
  CheckCircle2,
  Lock,
  Plug,
  Database,
  Bell,
  BarChart3,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Container, SectionHeading } from "@/components/common/Section";
import Reveal from "@/components/common/Reveal";
import CtaBand from "@/components/common/CtaBand";
import LicaraMock from "@/components/common/LicaraMock";

// Licara sub-brand tokens (navy + forest green)
const NAVY = "#1E3A64";
const GREEN = "#1E7A46";

const modules = [
  {
    icon: FileCheck2,
    name: "Permits & Licensing",
    tagline: "Digital-first, application to renewal",
    features: [
      "Online application intake & document uploads",
      "Configurable approval workflows & fees",
      "Automated renewals & expiration reminders",
      "Inspection scheduling and checklists",
    ],
  },
  {
    icon: MapPin,
    name: "Field Officer & Citations",
    tagline: "Enforcement that works in the field",
    features: [
      "Mobile citation capture with photo evidence",
      "Geolocation and map-based assignment",
      "Offline mode with automatic sync",
      "Real-time status updates to the office",
    ],
  },
  {
    icon: Scale,
    name: "Case Management",
    tagline: "Intake to resolution, fully tracked",
    features: [
      "Unified case files with full history",
      "Escalations, deadlines & hearing schedules",
      "Notices, violations & compliance tracking",
      "Complete, exportable audit trail",
    ],
  },
];

const platform = [
  { icon: Lock, title: "Role-based security", desc: "Granular permissions and full audit logging." },
  { icon: Plug, title: "API-first integrations", desc: "GIS, payments, identity, and records systems." },
  { icon: Database, title: "Single source of truth", desc: "All records unified in one secure platform." },
  { icon: Bell, title: "Automated notifications", desc: "Reminders, escalations, and status alerts." },
  { icon: BarChart3, title: "Reporting & analytics", desc: "Dashboards for throughput and compliance." },
  { icon: CheckCircle2, title: "Compliance-ready", desc: "Designed around public-sector requirements." },
];

export default function Licara() {
  return (
    <div data-testid="licara-page">
      {/* LICARA-BRANDED HERO (navy) */}
      <section
        className="relative overflow-hidden"
        style={{ background: `linear-gradient(180deg, ${NAVY} 0%, hsl(220 35% 6%) 100%)` }}
      >
        <div className="bg-sentira-grid bg-radial-fade pointer-events-none absolute inset-0 opacity-40" />
        <div
          className="pointer-events-none absolute right-[-10%] top-[10%] h-72 w-72 rounded-full blur-3xl"
          style={{ background: "rgba(30,122,70,0.35)" }}
        />
        <Container className="relative py-20 md:py-28">
          <div className="grid items-center gap-12 lg:grid-cols-[1fr_1fr]">
            <div>
              <Reveal>
                <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs font-medium uppercase tracking-wider text-white/80">
                  <span className="h-1.5 w-1.5 rounded-full" style={{ background: GREEN }} />
                  Sentira Flagship Product
                </span>
              </Reveal>
              <Reveal delay={0.05}>
                <img
                  src="/brand/licara-logo-white.png"
                  alt="Licara"
                  className="mt-6 h-12 w-auto select-none md:h-14"
                  draggable="false"
                  data-testid="licara-logo"
                />
              </Reveal>
              <Reveal delay={0.1}>
                <h1 className="mt-6 text-3xl font-semibold leading-[1.1] tracking-[-0.02em] text-white sm:text-4xl">
                  The unified enforcement, permits & licensing platform
                </h1>
              </Reveal>
              <Reveal delay={0.15}>
                <p className="mt-5 max-w-xl text-base leading-relaxed text-white/70 md:text-lg">
                  Licara replaces spreadsheets and legacy systems with one secure
                  hub — so your team manages every permit, inspection, citation,
                  and case from a single source of truth.
                </p>
              </Reveal>
              <Reveal delay={0.2}>
                <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                  <Button
                    asChild
                    size="lg"
                    className="text-white hover:opacity-90"
                    style={{ background: GREEN }}
                  >
                    <Link to="/demo?interest=licara" data-testid="licara-demo-button">
                      Request a demo
                      <ArrowRight className="ml-1.5 h-4 w-4" />
                    </Link>
                  </Button>
                  <Button
                    asChild
                    size="lg"
                    variant="secondary"
                    className="border border-white/15 bg-white/10 text-white hover:bg-white/20"
                  >
                    <Link to="/contact" data-testid="licara-contact-button">Talk to sales</Link>
                  </Button>
                </div>
              </Reveal>
            </div>
            <Reveal delay={0.15} y={24}>
              <LicaraMock />
            </Reveal>
          </div>
        </Container>
      </section>

      {/* MODULES */}
      <section className="py-20 md:py-28">
        <Container>
          <SectionHeading
            eyebrow="Core modules"
            title="Three capabilities, one connected platform"
            description="Each module works on its own or together — sharing data, workflows, and a single audit trail."
          />
          <div className="mt-14 grid gap-5 lg:grid-cols-3">
            {modules.map((m, i) => (
              <Reveal key={m.name} delay={i * 0.08}>
                <div className="flex h-full flex-col rounded-[var(--radius-card)] border border-border bg-card p-7 shadow-[var(--shadow-card)] transition-colors hover:border-[rgba(30,122,70,0.5)]">
                  <div
                    className="flex h-11 w-11 items-center justify-center rounded-xl border border-border"
                    style={{ background: "rgba(30,122,70,0.12)" }}
                  >
                    <m.icon className="h-5 w-5" style={{ color: GREEN }} />
                  </div>
                  <h3 className="mt-5 text-lg font-semibold">{m.name}</h3>
                  <p className="mt-1 text-sm" style={{ color: GREEN }}>{m.tagline}</p>
                  <ul className="mt-5 space-y-3">
                    {m.features.map((f) => (
                      <li key={f} className="flex items-start gap-2.5 text-sm text-muted-foreground">
                        <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0" style={{ color: GREEN }} />
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* PLATFORM FOUNDATION */}
      <section className="pb-20 md:pb-28">
        <Container>
          <div className="rounded-[24px] border border-border bg-[hsl(220_30%_8%)] p-8 md:p-14">
            <SectionHeading eyebrow="Platform foundation" title="Enterprise-grade by default" align="center" />
            <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {platform.map((p, i) => (
                <Reveal key={p.title} delay={i * 0.05}>
                  <div className="h-full rounded-xl border border-border bg-card p-5">
                    <p.icon className="h-5 w-5" style={{ color: GREEN }} />
                    <h4 className="mt-3 text-base font-semibold">{p.title}</h4>
                    <p className="mt-1.5 text-sm text-muted-foreground">{p.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </Container>
      </section>

      <CtaBand
        title="See Licara in action"
        description="Book a personalized walkthrough tailored to your agency's workflows."
        primaryTo="/demo?interest=licara"
      />
    </div>
  );
}
